import React from 'react';
import { Box, Typography, IconButton, Divider, useMediaQuery } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import ReactPlayer from 'react-player/lazy';

interface RightPreviewProps {
  file: {
    id: string;
    type: string;
    url: string;
    name: string;
    description: string;
    size: string;
    createdAt: string;
  };
  onClose: () => void;
}

const RightPreview: React.FC<RightPreviewProps> = ({ file, onClose }) => {
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        width: isMobile ? '100%' : '30%',
        maxWidth: '400px',
        position: 'fixed',
        top: '64px',
        right: 0,
        height: '100vh',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        padding: 2,
        borderLeft: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        boxShadow: '0px 4px 20px rgba(0,0,0,0.1)',
        overflowY: 'auto',
      }}
    >
      <motion.div
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 100 }}
        transition={{ duration: 0.5 }}
      >
        {/* Media Content */}
        <Box
          sx={{
            width: '100%',
            marginBottom: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            maxHeight: '600px',
            overflow: 'hidden',
            bgcolor: 'background.default',
            // p: '40px',
          }}
        >
          {file.type === 'image' &&
            <img
              src={`https://dprstorage.b-cdn.net${file.url}`}
              alt={file.name}
              style={{
                width: '100%',
                height: '100%',
                maxHeight: '300px',
                objectFit: 'contain', // Ensures the image fits perfectly in the center
                pointerEvents: 'none',
                userSelect: 'none',
              }}
              onContextMenu={(e) => e.preventDefault()}
              draggable={false}
            />
          }
          {file.type === 'video' && (
            <ReactPlayer
                url={`https://dprstorage.b-cdn.net${file.url}`}
                controls
                muted={false} // Start with sound enabled; the user can toggle mute/unmute
                width="100%"
                height="100%"
                style={{
                  objectFit: 'contain', // Ensures the video fits perfectly in the center
                  padding: '10px 30px',
                }}
                config={{
                  file: {
                    attributes: {
                      controlsList: 'nodownload', // Prevents downloading
                    },
                  },
                }}
              />
          )}
        </Box>

        <Divider sx={{ marginBottom: 2 }} />

        {/* File Details */}
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: 2 }}>
            {file.name}
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 2, color: 'text.secondary' }}>
            {file.description}
          </Typography>
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
              <strong>File Type:</strong> {file.type}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
              <strong>Size:</strong> {file.size}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Created At:</strong> {new Date(file.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            bgcolor: 'secondary.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'secondary.dark',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </motion.div>
    </Box>
  );
};

export default RightPreview;